import { collection, addDoc } from 'firebase/firestore';
import { db } from './firebase';
import { trackEvent, trackPageView } from './analyticsTracker';

const ANALYTICS_COLLECTION = 'analytics';

/** Video view event: fired when user watches ≥70% of a video */
export interface VideoViewEvent {
    mediaID: string;
    libraryId: string;
    progress: number; // 0.7 or 1
    watchedAt: string; // UTC ISO
    source: 'home' | 'grow' | 'exercise';
}

/** Session start event: fired once per browser tab session when user is authenticated */
export interface SessionStartEvent {
    startedAt: string; // UTC ISO
}

/** Page view event: fired when user navigates to a view */
export interface PageViewEvent {
    view: string;
    viewedAt: string; // UTC ISO
}

/**
 * Record a video view event (e.g. at 70% or 100% watched).
 * Path: analytics/{uid}/videoViewEvents/{eventId}
 */
export const recordVideoViewEvent = async (
    uid: string,
    event: Omit<VideoViewEvent, 'watchedAt'>
): Promise<void> => {
    trackEvent('video_view', {
        media_id: event.mediaID,
        library_id: event.libraryId,
        progress: event.progress,
        source: event.source,
    });
    try {
        const ref = collection(db, ANALYTICS_COLLECTION, uid, 'videoViewEvents');
        await addDoc(ref, {
            ...event,
            watchedAt: new Date().toISOString(),
        });
    } catch (error) {
        console.error('Analytics: failed to record video view event:', error);
    }
};

/**
 * Record a session start event.
 * Path: analytics/{uid}/sessionStartEvents/{eventId}
 */
export const recordSessionStartEvent = async (uid: string): Promise<void> => {
    trackEvent('session_start');
    try {
        const ref = collection(db, ANALYTICS_COLLECTION, uid, 'sessionStartEvents');
        await addDoc(ref, {
            startedAt: new Date().toISOString(),
        });
    } catch (error) {
        console.error('Analytics: failed to record session start event:', error);
    }
};

/**
 * Record a page view event.
 * Path: analytics/{uid}/pageViewEvents/{eventId}
 */
export const recordPageViewEvent = async (
    uid: string,
    event: Omit<PageViewEvent, 'viewedAt'>
): Promise<void> => {
    trackPageView(event.view);
    try {
        const ref = collection(db, ANALYTICS_COLLECTION, uid, 'pageViewEvents');
        await addDoc(ref, {
            ...event,
            viewedAt: new Date().toISOString(),
        });
    } catch (error) {
        console.error('Analytics: failed to record page view event:', error);
    }
};

/** Notification open event: fired when user taps a push notification */
export interface NotificationOpenEvent {
    templateId?: string;
    title?: string;
    screen?: string; // deep link target
    openedAt: string; // UTC ISO
}

/**
 * Record a notification open event (user tapped the notification).
 * Path: analytics/{uid}/notificationOpenEvents/{eventId}
 */
export const recordNotificationOpenEvent = async (
    uid: string,
    event: Omit<NotificationOpenEvent, 'openedAt'>
): Promise<void> => {
    trackEvent('notification_open', {
        template_id: event.templateId ?? '',
        title: event.title ?? '',
        screen: event.screen ?? '',
    });
    try {
        const ref = collection(db, ANALYTICS_COLLECTION, uid, 'notificationOpenEvents');
        await addDoc(ref, {
            ...event,
            openedAt: new Date().toISOString(),
        });
    } catch (error) {
        console.error('Analytics: failed to record notification open event:', error);
    }
};

/**
 * Notification preference change event.
 *
 * Preferences are written in place (setDoc merge), so before this there was no
 * record that a change ever happened — only its end state. We log the change as
 * its own timestamped event and join it against the notification ledger at read
 * time ("did a send precede this opt-out?"). Deliberately NOT stamped onto the
 * ledger rows: that would bake a fixed correlation window into the data, and the
 * window is exactly the thing we'll want to vary once we look at it.
 */
export interface NotificationPrefChangeEvent {
    /** Preference state before the change, for computing what actually flipped. */
    before: Record<string, unknown>;
    after: Record<string, unknown>;
    /** True when this change turns notifications off entirely — the one we care about most. */
    disabled: boolean;
    changedAt: string; // UTC ISO
}

/**
 * Record a notification preference change.
 * Path: analytics/{uid}/notificationPrefChangeEvents/{eventId}
 */
export const recordNotificationPrefChangeEvent = async (
    uid: string,
    event: Omit<NotificationPrefChangeEvent, 'changedAt'>
): Promise<void> => {
    trackEvent('notification_pref_change', { disabled: String(event.disabled) });
    try {
        const ref = collection(db, ANALYTICS_COLLECTION, uid, 'notificationPrefChangeEvents');
        await addDoc(ref, {
            ...event,
            changedAt: new Date().toISOString(),
        });
    } catch (error) {
        console.error('Analytics: failed to record notification pref change event:', error);
    }
};

/** Feature use event: which features actually get used (beyond page views) */
export interface FeatureUseEvent {
    feature: string; // 'chatcoach' | 'goals' | 'journal' | 'cart_companion' | ...
    action: string;  // 'open' | 'message' | 'create' | 'add' | ...
    usedAt: string;  // UTC ISO
    /** Optional event dimensions (e.g. paywall {source, product}) — rolled up by the nightly funnels. */
    meta?: Record<string, string>;
}

const FEATURE_USE_SESSION_PREFIX = 'enough_feature_use_';

/**
 * Record a feature use event. Throttled to once per tab session per
 * feature+action by default so high-frequency actions (chat messages)
 * count engagement sessions, not raw volume.
 * Path: analytics/{uid}/featureUseEvents/{eventId}
 */
export const recordFeatureUseEvent = async (
    uid: string,
    feature: string,
    action: string = 'open',
    options: {
        oncePerSession?: boolean;
        /** Extra dimensions stored on the event doc (nullish values dropped). */
        meta?: Record<string, string | null | undefined>;
    } = {},
): Promise<void> => {
    const { oncePerSession = true } = options;
    if (oncePerSession) {
        const key = `${FEATURE_USE_SESSION_PREFIX}${uid}_${feature}_${action}`;
        try {
            if (sessionStorage.getItem(key)) return;
            sessionStorage.setItem(key, '1');
        } catch { /* storage unavailable — record anyway */ }
    }
    const meta: Record<string, string> = {};
    for (const [key, value] of Object.entries(options.meta ?? {})) {
        if (value != null) meta[key] = value;
    }
    const hasMeta = Object.keys(meta).length > 0;
    trackEvent('feature_use', { feature, action, ...meta });
    try {
        const ref = collection(db, ANALYTICS_COLLECTION, uid, 'featureUseEvents');
        await addDoc(ref, {
            feature,
            action,
            usedAt: new Date().toISOString(),
            ...(hasMeta ? { meta } : {}),
        });
    } catch (error) {
        console.error('Analytics: failed to record feature use event:', error);
    }
};

/** Exercise start event: fired when user opens an exercise module */
export interface ExerciseStartEvent {
    exerciseId: string;
    moduleId: string;
    startedAt: string; // UTC ISO
}

/**
 * Record an exercise start event (user opened the exercise view).
 * Path: analytics/{uid}/exerciseStartEvents/{eventId}
 */
export const recordExerciseStartEvent = async (
    uid: string,
    event: Omit<ExerciseStartEvent, 'startedAt'>
): Promise<void> => {
    trackEvent('exercise_start', {
        exercise_id: event.exerciseId,
        module_id: event.moduleId,
    });
    try {
        const ref = collection(db, ANALYTICS_COLLECTION, uid, 'exerciseStartEvents');
        await addDoc(ref, {
            ...event,
            startedAt: new Date().toISOString(),
        });
    } catch (error) {
        console.error('Analytics: failed to record exercise start event:', error);
    }
};
